import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { useUrlTenant } from '../../hooks/useUrlTenant';
import { AppearanceDialog } from './AppearanceDialog';
import { LanguageSwitch } from './LanguageSwitch';

/**
 * UserMenu — user info, operator nav (workspaces / reports / personas / canned responses /
 * contacts), logout. This is the operator dashboard's only surface; the former hub admin
 * nav was removed when the hub UI moved to the standalone integration_hub app.
 */
export function UserMenu() {
  const { userInfo, logout, isGlobalAdmin, canIn } = useAuth();
  const { t } = useTranslation();
  const { tenants } = useTenant();
  const navigate = useNavigate();
  const currentTenant = useUrlTenant();
  // Workspaces: multi-workspace users, and global admins (who can open any workspace).
  const showTenants = isGlobalAdmin || tenants.length > 1;
  // Reports: admins/owners of the current workspace (the gateway enforces the same rule).
  const showReports = !!currentTenant && canIn('viewReports', currentTenant.id);
  const [isOpen, setIsOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!userInfo) return null;

  const info = typeof userInfo === 'function' ? userInfo() : userInfo;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 sm:gap-3 min-h-11 hover:bg-surface-2 rounded-control transition-colors px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t('menu.userMenu')}
      >
        {info.picture ? (
          <img
            src={info.picture}
            alt={info.name || t('common.user')}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-avatar border border-line"
          />
        ) : (
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-avatar bg-tint flex items-center justify-center text-on-tint text-sm font-semibold">
            {(info.name || info.email || 'O').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="text-left hidden lg:block">
          <p className="text-sm font-medium text-ink leading-tight">
            {info.name || info.email || t('common.operator')}
          </p>
          {info.email && (
            <p className="text-xs text-muted leading-tight">{info.email}</p>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-muted transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Mobile: Full-screen backdrop */}
          <div
            className="sm:hidden fixed inset-0 bg-scrim z-[99]"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown Menu */}
          <div className="absolute top-full right-0 mt-2 w-56 bg-surface border border-line rounded-card shadow-pop z-[100] sm:z-[100]">
            <div className="py-2">
              {/* User Info Section */}
              <div className="px-4 py-3 border-b border-line flex items-center gap-3">
                {info.picture ? (
                  <img
                    src={info.picture}
                    alt={info.name || t('common.user')}
                    className="w-10 h-10 rounded-avatar border border-line flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-avatar bg-tint flex items-center justify-center text-on-tint text-sm font-semibold flex-shrink-0">
                    {(info.name || info.email || 'O').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="text-left min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink truncate">
                    {info.name || info.email || t('common.operator')}
                  </p>
                  {info.email && (
                    <p className="text-xs text-muted mt-0.5 truncate">{info.email}</p>
                  )}
                </div>
              </div>

              <LanguageSwitch />

              {showTenants && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/tenants');
                  }}
                  className={`w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    location.pathname === '/tenants' ? 'bg-tint text-on-tint font-medium' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
                    </svg>
                    <span>{isGlobalAdmin ? t('menu.allWorkspaces') : t('menu.workspaces')}</span>
                  </div>
                </button>
              )}

              {showReports && currentTenant && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate(`/${currentTenant.slug}/reports`);
                  }}
                  className={`w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    location.pathname.endsWith('/reports') ? 'bg-tint text-on-tint font-medium' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V9m6 10V5m-12 14h18" />
                    </svg>
                    <span>{t('menu.reports')}</span>
                  </div>
                </button>
              )}

              {/* My Personas */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/personas');
                }}
                className={`w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  location.pathname === '/personas' ? 'bg-tint text-on-tint font-medium' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{t('menu.personas')}</span>
                </div>
              </button>

              {/* Canned Responses */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/canned-responses');
                }}
                className={`w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  location.pathname === '/canned-responses' ? 'bg-tint text-on-tint font-medium' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z" />
                  </svg>
                  <span>{t('menu.canned')}</span>
                </div>
              </button>

              {/* Contacts (segments) */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/contacts');
                }}
                className={`w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  location.pathname === '/contacts' ? 'bg-tint text-on-tint font-medium' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-3-6.7" />
                  </svg>
                  <span>{t('menu.contacts')}</span>
                </div>
              </button>

              {/* Appearance — theme for everyone; the look for workspace/global admins */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setAppearanceOpen(true);
                }}
                className="w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                    <path d="M12 3a9 9 0 000 18z" fill="currentColor" />
                  </svg>
                  <span>{t('appearance.menu')}</span>
                </div>
              </button>

              {/* Logout */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  logout();
                }}
                className="w-full justify-start text-left px-4 min-h-11 text-sm text-ink hover:bg-surface-2 transition-colors border-t border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {t('menu.logout')}
              </button>
            </div>
          </div>
        </>
      )}
      {appearanceOpen && <AppearanceDialog onClose={() => setAppearanceOpen(false)} />}
    </div>
  );
}
